import { useState, useEffect } from 'react';
import Badge from '../shared/Badge';
import MarkdownEditor from '../shared/MarkdownEditor';
import { IconButton } from '../shared/StandardButtons';
import { adminApi } from '../../lib/admin-api';

export default function AIParametersModal({ isOpen, onClose }) {
  const [systemParameters, setSystemParameters] = useState([]);
  const [customParameters, setCustomParameters] = useState([]);
  const [availableVariables, setAvailableVariables] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Section collapse state
  const [isCustomSectionExpanded, setIsCustomSectionExpanded] = useState(true);
  const [isSystemSectionExpanded, setIsSystemSectionExpanded] = useState(false);

  // Expanded parameter (only for custom parameters)
  const [expandedParameterId, setExpandedParameterId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  // Create/Edit form state
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    is_template: false
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadParameters();
    }
  }, [isOpen]);

  const loadParameters = async () => {
    setIsLoading(true);
    try {
      const data = await adminApi.promptParameters.getAll();
      setSystemParameters(data.system_parameters || []);
      setCustomParameters(data.custom_parameters || []);
      setAvailableVariables(data.available_variables || []);
    } catch (error) {
      console.error('Failed to load parameters:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = (paramId) => {
    setExpandedParameterId(expandedParameterId === paramId ? null : paramId);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(formData.name)) {
      errors.name = 'Name must start with a letter and contain only letters, numbers, and underscores';
    }

    if (!formData.content.trim()) {
      errors.content = 'Content is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = () => {
    setFormData({ name: '', description: '', content: '', is_template: false });
    setFormErrors({});
    setIsCreating(true);
    setEditingId(null);
  };

  const handleEdit = (param) => {
    setFormData({
      name: param.name,
      description: param.description || '',
      content: param.value,
      is_template: param.type === 'template'
    });
    setFormErrors({});
    setEditingId(param.id);
    setIsCreating(false);
  };

  const handleCancelForm = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({ name: '', description: '', content: '', is_template: false });
    setFormErrors({});
  };

  const handleSaveParameter = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (editingId) {
        // Update existing
        await adminApi.promptParameters.update(editingId, {
          description: formData.description,
          content: formData.content,
          is_template: formData.is_template
        });
      } else {
        // Create new
        await adminApi.promptParameters.create(formData);
      }

      await loadParameters();
      handleCancelForm();
    } catch (error) {
      console.error('Failed to save parameter:', error);
      if (error.response?.data?.detail) {
        setFormErrors({ submit: error.response.data.detail });
      } else {
        setFormErrors({ submit: 'Failed to save parameter. Please try again.' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (paramId) => {
    try {
      await adminApi.promptParameters.delete(paramId);
      await loadParameters();
    } catch (error) {
      console.error('Failed to delete parameter:', error);
      alert('Failed to delete parameter');
    }
  };

  if (!isOpen) return null;

  // Filter parameters by search
  const filterBySearch = (params) => {
    if (!searchQuery) return params;
    return params.filter(param =>
      param.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      param.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const filteredCustomParameters = filterBySearch(customParameters);
  const filteredSystemParameters = filterBySearch(systemParameters);

  // Show form if creating or editing
  if (isCreating || editingId) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }} onClick={handleCancelForm}>
        <div className="bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 border border-gray-700 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="p-6 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                {editingId ? 'Edit Custom Parameter' : 'Create Custom Parameter'}
              </h3>
              <IconButton onClick={handleCancelForm} title="Close">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </IconButton>
            </div>
          </div>

          {/* Form */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">
                  Parameter Name {!editingId && <span className="text-red-400">*</span>}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!!editingId}
                  placeholder="e.g., role_based_guidelines"
                  className={`w-full px-4 py-2 bg-gray-900 border ${formErrors.name ? 'border-red-500' : 'border-gray-700'} rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed`}
                />
                {formErrors.name && (
                  <p className="text-red-400 text-xs mt-1">{formErrors.name}</p>
                )}
                {!editingId && (
                  <p className="text-gray-500 text-xs mt-1">
                    Use {'{{'}parameter_name{'}}'} in prompts. Must start with letter, contain only letters/numbers/underscores.
                  </p>
                )}
              </div>

              {/* Description Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What this parameter is for"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>

              {/* Template Toggle */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={formData.is_template}
                  onChange={(e) => setFormData({ ...formData, is_template: e.target.checked })}
                  className="mt-1 w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                />
                <div className="flex-1">
                  <label className="text-sm font-semibold text-gray-200 cursor-pointer">
                    Use template (dynamic content based on context)
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Enables Jinja2 templating with access to safe variables
                  </p>
                </div>
              </div>

              {/* Available Variables */}
              {formData.is_template && (
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <p className="text-sm font-semibold text-gray-300 mb-2">
                    Available template variables:
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {availableVariables.map(v => (
                      <code key={v} className="px-2 py-1 bg-gray-800 rounded text-xs text-blue-400 border border-gray-700">
                        {`{{ ${v} }}`}
                      </code>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    Example: {`{% if user_role == 'admin' %}`}Admin instructions{`{% else %}`}User instructions{`{% endif %}`}
                  </p>
                </div>
              )}

              {/* Content Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-2">
                  Content <span className="text-red-400">*</span>
                </label>
                <div className={`h-[400px] ${formErrors.content ? 'ring-2 ring-red-500 rounded-lg' : ''}`}>
                  <MarkdownEditor
                    content={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    externalControls={true}
                    isEditing={true}
                    showBorder={true}
                    textareaClassName="h-full"
                    placeholder={formData.is_template
                      ? "{% if user_role == 'admin' %}\nAdmin instructions\n{% else %}\nUser instructions\n{% endif %}"
                      : "Static content here..."
                    }
                    fontSize="text-sm"
                  />
                </div>
                {formErrors.content && (
                  <p className="text-red-400 text-xs mt-1">{formErrors.content}</p>
                )}
              </div>

              {/* Submit Error */}
              {formErrors.submit && (
                <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
                  <p className="text-red-400 text-sm">{formErrors.submit}</p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-700 flex justify-end gap-2 flex-shrink-0">
            <button
              onClick={handleCancelForm}
              disabled={isSaving}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-lg transition-all duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveParameter}
              disabled={isSaving}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 rounded-lg transition-all duration-200 flex items-center gap-2"
              style={{ color: 'var(--color-text-on-accent)' }}
            >
              {isSaving && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isSaving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main list view with collapsible sections
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }} onClick={onClose}>
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 border border-gray-700 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">AI Instruction Parameters</h3>
              <p className="text-gray-400 text-sm mt-1">Manage system and custom parameters for all prompts</p>
            </div>
            <IconButton onClick={onClose} title="Close">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </IconButton>
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search parameters..."
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
            <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Parameter Sections */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {/* Custom Parameters Section */}
              <div className="border-b border-gray-700">
                {/* Section Header */}
                <div className="w-full flex items-center justify-between p-4 hover:bg-gray-750 transition-colors">
                  <div
                    onClick={() => setIsCustomSectionExpanded(!isCustomSectionExpanded)}
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                  >
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${isCustomSectionExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <div className="text-left">
                      <h4 className="text-white font-semibold flex items-center gap-2">
                        Custom Parameters
                        <Badge variant="info">{filteredCustomParameters.length}</Badge>
                      </h4>
                      <p className="text-sm text-gray-400">Company-specific parameters you can create and edit</p>
                    </div>
                  </div>
                  <button
                    onClick={handleCreate}
                    className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-sm rounded-lg transition-all duration-200 flex items-center gap-1"
                    style={{ color: 'var(--color-text-on-accent)' }}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create
                  </button>
                </div>

                {/* Custom Parameters List */}
                {isCustomSectionExpanded && (
                  <div className="border-l-4 border-blue-500/30 ml-4">
                    {filteredCustomParameters.length === 0 ? (
                      <div className="text-center py-8 px-4 text-gray-500">
                        <p className="text-sm">
                          {searchQuery ? 'No custom parameters match your search' : 'No custom parameters yet. Click "Create" to add one.'}
                        </p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-700/50 pl-4">
                        {filteredCustomParameters.map((param) => {
                          const isExpanded = expandedParameterId === param.id;
                          return (
                            <div key={param.id}>
                              {/* Parameter Header */}
                              <div
                                onClick={() => toggleExpand(param.id)}
                                className="flex items-center gap-3 p-4 transition-colors cursor-pointer"
                                style={{
                                  backgroundColor: 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <div className="text-gray-400">
                                  <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h5 className="text-white font-medium truncate">{param.name}</h5>
                                  {param.description && (
                                    <p className="text-sm text-gray-400 truncate">{param.description}</p>
                                  )}
                                </div>
                                <Badge variant="default">{param.type}</Badge>

                                {/* Delete Button */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(param.id);
                                  }}
                                  className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                  title="Delete parameter"
                                >
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>

                              {/* Expanded Details */}
                              {isExpanded && (
                                <div className="p-4">
                                  <div className="border border-gray-700 rounded-lg p-4 space-y-4">
                                    <div>
                                      <label className="block text-sm font-semibold text-gray-200 mb-2">Content</label>
                                      <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                                        <pre className="text-sm text-gray-300 font-mono whitespace-pre-wrap break-words">
                                          {param.value || '(No content)'}
                                        </pre>
                                      </div>
                                    </div>
                                    <div className="flex justify-end">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEdit(param);
                                        }}
                                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-sm rounded-lg transition-all duration-200 flex items-center gap-1"
                                        style={{ color: 'var(--color-text-on-accent)' }}
                                      >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* System Parameters Section */}
              <div>
                {/* Section Header */}
                <div
                  onClick={() => setIsSystemSectionExpanded(!isSystemSectionExpanded)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-750 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <svg className={`w-5 h-5 text-gray-400 transition-transform ${isSystemSectionExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <div className="text-left">
                      <h4 className="text-white font-semibold flex items-center gap-2">
                        System Parameters
                        <Badge variant="warning">{filteredSystemParameters.length}</Badge>
                      </h4>
                      <p className="text-sm text-gray-400">Hardcoded system parameters (read-only)</p>
                    </div>
                  </div>
                  <div className="text-xs flex items-center gap-1" style={{ color: 'var(--color-warning)' }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Read Only
                  </div>
                </div>

                {/* System Parameters List */}
                {isSystemSectionExpanded && (
                  <div className="border-l-4 border-amber-500/30 ml-4">
                    {filteredSystemParameters.length === 0 ? (
                      <div className="text-center py-8 px-4 text-gray-500">
                        <p className="text-sm">No system parameters match your search</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-700/50 pl-4">
                        {filteredSystemParameters.map((param) => (
                          <div key={param.name} className="flex items-center gap-3 p-4">
                            {/* No chevron - not expandable */}
                            <div className="w-5"></div>
                            <div className="flex-1 min-w-0">
                              <h5 className="text-white font-medium truncate">{param.name}</h5>
                              {param.description && (
                                <p className="text-sm text-gray-400 truncate">{param.description}</p>
                              )}
                            </div>
                            <Badge variant="default">{param.type}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
